import {
  Alert,
  Center,
  DrawerBody,
  DrawerHeader,
  Flex,
  HStack,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useCallback, useMemo } from 'react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { UserAvatar } from '@duke-hq/ui/components/UserAvatar/UserAvatar';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { constructQueryParams, getUserFullName } from '@duke-hq/lib';
import { ProviderConsultantSettings } from './ProviderConsultantsSettings';
import { ProviderConsultantSiteAccess } from './ProviderConsultantSiteAccess';
import { ProviderConsultantPermissions } from './ProviderConsultantPermissions';
import { ProviderConsultantForm } from './ProviderConsultantForm';
import { LocationError } from '../Locations/LocationError';
import { NoteList } from '../../Notes';
import { LoginAsButton } from '../../components/LoginAsButton';

interface URLParams extends Record<string, string> {
  providerId: string;
  consultantId: string;
  tab: string;
}

export function ProviderConsultantDetails() {
  const { providerId, consultantId, tab } = useParams<URLParams>();
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();

  const tabs = useMemo(
    () =>
      [
        { value: 'details', label: t`Details` },
        { value: 'access', label: t`Access` },
        { value: 'permissions', label: t`Permissions` },
        { value: 'notes', label: t`Notes` },
        { value: 'settings', label: t`Settings` },
      ] as const,
    [],
  );

  const selectedTab = useMemo(() => {
    if (typeof tab !== 'string') {
      return 0;
    }

    const tabIdx = tabs.findIndex((tabObj) => tabObj.value === tab);
    return tabIdx !== -1 ? tabIdx : 0;
  }, [tab, tabs]);

  const getPreserveUrl = useMemo(() => {
    const urlFilters = Object.fromEntries(searchParams.entries());
    const { page, status, ...rest } = urlFilters;

    const URLFilters = {
      ...rest,
      status: status || undefined,
      page: page ?? '1',
    };

    return `${constructQueryParams({ ...URLFilters })}`;
  }, [searchParams]);

  const handleTabsChange = useCallback(
    (index: number) => {
      if (typeof tabs[index] !== 'undefined') {
        navigate(
          `/providers/${providerId}/consultants/${consultantId}/${tabs[index]?.value}${getPreserveUrl}`,
        );
      }
    },
    [navigate, providerId, consultantId, getPreserveUrl, tabs],
  );

  if (providerId == null || consultantId == null) {
    return (
      <LocationError message={t`An error occurred, providerId and consultantId are required.`} />
    );
  }

  const { data: member, isLoading } = trpcAdmin.providers.user.consultantById.useQuery(
    {
      providerId,
      id: consultantId,
    },
    { enabled: Boolean(!!providerId && !!consultantId) },
  );

  if (isLoading || member == null) {
    return (
      <Center h="100vh">
        <Spinner size="xl" />
      </Center>
    );
  }

  if (member == null) {
    return (
      <Alert>
        <Trans>No consultant found</Trans>
      </Alert>
    );
  }

  return (
    <Flex flexDirection="column" w="100%" h="100%">
      <DrawerHeader
        borderBottomWidth="1px"
        borderBottomColor="border.default"
        p={{ base: 2, md: 4 }}
      >
        <HStack justify="space-between" alignContent="center">
          <HStack align="start" h="full" alignSelf="stretch">
            <UserAvatar
              user={{
                first_name: member?.first_name,
                last_name: member?.last_name,
                picture_url: member?.picture_url,
              }}
              size="lg"
            />
            <VStack ml="16px" align={'start'} spacing={0}>
              <HStack gap={1} align="center">
                <Text fontWeight="bold">
                  {member != null
                    ? getUserFullName({
                        first_name: member?.first_name,
                        last_name: member?.last_name,
                        picture_url: member?.picture_url,
                      })
                    : null}
                </Text>
                <LoginAsButton
                  kind="consultant"
                  consultantId={consultantId}
                  providerId={Number(providerId)}
                />
              </HStack>
            </VStack>
          </HStack>
        </HStack>
      </DrawerHeader>

      <DrawerBody p="0" border="none" m="0" paddingInline="0 !important">
        <Tabs
          isLazy
          flexDirection="column"
          display="flex"
          w="100%"
          h="100%"
          index={selectedTab}
          onChange={handleTabsChange}
        >
          <TabList bg="bg.subtle">
            {tabs.map((tab, index) => (
              <Tab
                key={tab.value}
                bg={selectedTab === index ? 'bg.surface' : ''}
                _hover={{ bg: 'bg.muted' }}
              >
                {tab.label}
              </Tab>
            ))}
          </TabList>

          <TabPanels width="100%" height="100%" overflow="auto">
            {tabs.map((tab) => (
              <TabPanel key={tab.value} width="100%" height="100%" overflow="scroll" p={0}>
                {member != null ? (
                  <>
                    {tab.value === 'details' && (
                      <ProviderConsultantForm
                        providerId={providerId}
                        member={member}
                        allowLocationSelection={false}
                      />
                    )}
                    {tab.value === 'access' && (
                      <ProviderConsultantSiteAccess providerId={providerId} member={member} />
                    )}
                    {tab.value === 'permissions' && (
                      <ProviderConsultantPermissions providerId={providerId} member={member} />
                    )}
                    {tab.value === 'notes' && (
                      <NoteList tenantId={Number(providerId)} userDrn={member.drn} />
                    )}
                    {tab.value === 'settings' && (
                      <ProviderConsultantSettings providerId={providerId} member={member} />
                    )}
                  </>
                ) : null}
              </TabPanel>
            ))}
          </TabPanels>
        </Tabs>
      </DrawerBody>
    </Flex>
  );
}
